import React from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';

import './ConfirmDialog.scss';

/**
 * ConfirmDialog — a single, theme-driven confirmation dialog used across the
 * app so every confirm/destructive prompt looks and behaves the same.
 *
 * Styling is driven entirely by the app theme variables (light/dark aware),
 * with a `variant` controlling the accent (danger / primary / warning).
 *
 * Props:
 *  - show           : boolean — visibility
 *  - onHide         : () => void — close/dismiss handler (backdrop, Esc, Cancel)
 *  - onConfirm      : () => void — confirm action
 *  - title          : string
 *  - message        : string | node
 *  - confirmLabel   : string (default "Confirm")
 *  - cancelLabel    : string (default "Cancel")
 *  - variant        : 'danger' | 'primary' | 'warning' (default 'primary')
 *  - icon           : icofont class (defaults per variant)
 *  - loading        : boolean — shows spinner + disables confirm
 */

const VARIANT_ICON = {
  danger: 'icofont-warning-alt',
  warning: 'icofont-warning',
  primary: 'icofont-question-circle'
};

function ConfirmDialog({
  show,
  onHide,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'primary',
  icon,
  loading = false
}) {
  const resolvedIcon = icon || VARIANT_ICON[variant] || VARIANT_ICON.primary;

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className={`app-confirm app-confirm--${variant}`}
      contentClassName="app-confirm__content border-0"
    >
      <Modal.Body className="app-confirm__body text-center">
        <span className="app-confirm__icon" aria-hidden>
          <i className={resolvedIcon} />
        </span>
        {title ? <h5 className="app-confirm__title">{title}</h5> : null}
        {message ? <p className="app-confirm__text">{message}</p> : null}
      </Modal.Body>
      <Modal.Footer className="app-confirm__footer border-0">
        <Button
          variant="light"
          className="app-confirm__btn app-confirm__btn--cancel"
          onClick={onHide}
          disabled={loading}
        >
          {cancelLabel}
        </Button>
        <Button
          variant={variant === 'primary' ? 'primary' : variant}
          className="app-confirm__btn app-confirm__btn--confirm"
          onClick={onConfirm}
          disabled={loading}
        >
          {loading ? <Spinner size="sm" className="me-2" /> : null}
          {confirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ConfirmDialog;
